import { mapState } from 'vuex';
import ApiService from '@/services/apiService';
import { get } from 'lodash';
import { getUnsyncedChanges } from '@/utils/deviceTemplateUtils';
export default {
  name: 'DeviceTemplatesUnsyncedChanges',
  props: {
    template: {
      type: Object,
      required: true
    },
    hasUnsavedChanges: {
      type: Boolean,
      required: true
    },
    linkedDevicesTotalCount: {
      type: Number,
      required: true
    },
    readonly: {
      type: Boolean,
      optional: true,
      default: false
    }
  },
  data() {
    return {
      allDevicesSynced: false,
      tableLoading: false,
      expanded: [],
      syncDialogOpen: false
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color', 'permissions']),
    tableLocalizations() {
      return {
        headers: [{
          sortable: false,
          text: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.modification'),
          value: 'modification'
        }, {
          sortable: false,
          text: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.valueBeforeSync'),
          value: 'valueBeforeSync'
        }, {
          sortable: false,
          text: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.valueAfterSync'),
          value: 'valueAfterSync'
        }]
      };
    },
    promptButtons() {
      const buttons = [{
        text: this.$i18n_t('Common.cancel'),
        emit: 'cancel'
      }];
      if (this.hasUnsavedChanges) {
        buttons.push({
          text: this.$i18n_t('Common.saveAndSynchronise'),
          color: this.color,
          emit: 'saveAndSynchronise'
        });
      } else {
        buttons.push({
          text: this.$i18n_t('Common.synchronise'),
          color: this.color,
          emit: 'saveAndSynchronise',
          dataCy: 'save_and_synchronise'
        });
      }
      return buttons;
    },
    /** Rows from the live template so the sync button matches the table (parent list can lag behind `slotProps.item`). */
    unsyncedChangeRows() {
      if (!this.template || typeof this.template !== 'object') {
        return [];
      }
      return getUnsyncedChanges(this.template).map((row, index) => ({
        ...row,
        _rowKey: index
      }));
    },
    showUnsyncedSection() {
      return this.linkedDevicesTotalCount !== 0 || this.unsyncedChangeRows.length > 0;
    },
    /**
     * Publish-and-sync does not require linked VDs. Only block on permission, explicit sync running, or no diff.
     * Use `isRunning === true` so non-boolean API values do not keep the button stuck off.
     */
    syncDisabled() {
      return this.readonly || get(this.template, 'deviceSyncStatus.isRunning') === true || this.unsyncedChangeRows.length === 0;
    },
    syncDisabledReason() {
      if (this.readonly) {
        return this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.syncDisabledReadonly');
      }
      if (get(this.template, 'deviceSyncStatus.isRunning') === true) {
        return this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.syncDisabledSyncInProgress');
      }
      if (this.unsyncedChangeRows.length === 0) {
        return this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.syncDisabledNoUnsyncedChanges');
      }
      return '';
    }
  },
  methods: {
    async saveAndSynchronise() {
      // in productive view mode `this.template` points to the productive version.
      // publish-and-sync needs the draft though.
      let draftTemplate = null;
      try {
        if (!this.hasUnsavedChanges) {
          draftTemplate = await ApiService.deviceTemplateGetByIdDraft(this.template.id);
        }
        const templateToSync = draftTemplate !== null && draftTemplate !== undefined ? draftTemplate : this.template;
        await ApiService.deviceTemplatePublishAndSync(templateToSync);
        this.$snack(this.$i18n_t('DigitalTwin.VirtualDevice.snacks.syncStarted'), 'success');
      } catch (e) {
        this.$snack(this.$i18n_t('DigitalTwin.VirtualDevice.snacks.syncError'), 'error');
      }
      if (!draftTemplate) {
        draftTemplate = await ApiService.deviceTemplateGetByIdDraft(this.template.id);
      }
      this.$emit('save-and-synchronise', draftTemplate);
      this.syncDialogOpen = false;
    }
  }
};